/**
 * FornecedoresPage Component
 * 
 * Página de listagem e gerenciamento de fornecedores.
 * Permite visualizar, criar, editar e excluir fornecedores.
 * 
 * Funcionalidades:
 * - Listagem de fornecedores com paginação
 * - Filtros de busca
 * - Modal de criação/edição
 * - Exclusão com confirmação
 * - Visualização de detalhes
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Edit, Trash2, Plus, Search, Upload, FileSpreadsheet, X, CheckCircle2, AlertCircle } from 'lucide-react'
import DataTable from '../../../../components/ERP/DataTable/DataTable'
import Modal from '../../../../components/ERP/Modal/Modal'
import FormField from '../../../../components/ERP/Form/FormField'
import { supplierService } from '../../../../services/erpApi'
import { usePermissions } from '../../../../hooks/usePermissions'
import { MODULES, ACTIONS } from '../../../../hooks/usePermissions'
import './FornecedoresPage.css'

function FornecedoresPage() {
  const navigate = useNavigate()
  const { hasPermission, loading: permissionsLoading } = usePermissions()
  const [fornecedores, setFornecedores] = useState([])
  const [loading, setLoading] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingFornecedor, setEditingFornecedor] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    cpfCnpj: '',
    addressStreet: '',
    addressNumber: '',
    addressComplement: '',
    addressNeighborhood: '',
    addressCity: '',
    addressState: '',
    addressZipcode: '',
    addressCountry: 'BR',
    type: 'F',
    status: 'active'
  })
  const [errors, setErrors] = useState({})
  const [searchTerm, setSearchTerm] = useState('')

  // Estados para importação Excel
  const [importModalOpen, setImportModalOpen] = useState(false)
  const [importStep, setImportStep] = useState('upload')
  const [importFile, setImportFile] = useState(null)
  const [importPreview, setImportPreview] = useState(null)
  const [importResult, setImportResult] = useState(null)
  const [importLoading, setImportLoading] = useState(false)
  const importFileInputRef = useRef(null)

  // Verificar permissão ao montar o componente
  useEffect(() => {
    if (!permissionsLoading) {
      if (!hasPermission(MODULES.PEOPLE, ACTIONS.PEOPLE_SUPPLIERS)) {
        console.warn('[FornecedoresPage] Usuário não tem permissão para acessar fornecedores')
        navigate('/erp/pessoas', { replace: true })
        return
      }
    }
  }, [permissionsLoading, hasPermission, navigate])

  const loadFornecedores = useCallback(async () => {
    setLoading(true)
    try {
      const response = await supplierService.getAll({ search: searchTerm })
      if (response.success) {
        setFornecedores(response.data || [])
      }
    } catch (error) {
      console.error('Erro ao carregar fornecedores:', error)
    } finally {
      setLoading(false)
    }
  }, [searchTerm])

  useEffect(() => {
    if (!permissionsLoading && hasPermission(MODULES.PEOPLE, ACTIONS.PEOPLE_SUPPLIERS)) {
      loadFornecedores()
    }
  }, [permissionsLoading, hasPermission, loadFornecedores])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setErrors({})

    try {
      const dataToSend = {
        name: formData.name,
        email: formData.email || null,
        phone: formData.phone || null,
        cpfCnpj: formData.cpfCnpj ? formData.cpfCnpj.replace(/\D/g, '') : null,
        addressStreet: formData.addressStreet || null,
        addressNumber: formData.addressNumber || null,
        addressComplement: formData.addressComplement || null,
        addressNeighborhood: formData.addressNeighborhood || null,
        addressCity: formData.addressCity || null,
        addressState: formData.addressState || null,
        addressZipcode: formData.addressZipcode ? formData.addressZipcode.replace(/\D/g, '') : null,
        addressCountry: formData.addressCountry || 'BR',
        type: formData.type || 'F',
        status: formData.status || 'active'
      }

      let response
      if (editingFornecedor) {
        response = await supplierService.update(editingFornecedor.id, dataToSend)
      } else {
        response = await supplierService.create(dataToSend)
      }

      if (response.success) {
        setModalOpen(false)
        resetForm()
        loadFornecedores()
      } else {
        if (response.errors && Array.isArray(response.errors)) {
          const errorObj = {}
          response.errors.forEach(err => {
            errorObj[err.param || err.path] = err.msg || err.message
          })
          setErrors(errorObj)
        } else {
          setErrors({ submit: response.message || 'Erro ao salvar fornecedor' })
        }
      }
    } catch (error) {
      console.error('Erro ao salvar fornecedor:', error)
      const errorMessage = error.response?.data?.message || 'Erro ao salvar fornecedor'
      const errorDetails = error.response?.data?.errors
      
      if (errorDetails && Array.isArray(errorDetails)) {
        const errorObj = {}
        errorDetails.forEach(err => {
          errorObj[err.param || err.path] = err.msg || err.message
        })
        setErrors(errorObj)
      } else {
        setErrors({ submit: errorMessage })
      }
    }
  }

  const handleEdit = (fornecedor) => {
    setEditingFornecedor(fornecedor)
    setFormData({
      name: fornecedor.name || '',
      email: fornecedor.email || '',
      phone: fornecedor.phone || '',
      cpfCnpj: fornecedor.cpf_cnpj || '',
      addressStreet: fornecedor.address_street || '',
      addressNumber: fornecedor.address_number || '',
      addressComplement: fornecedor.address_complement || '',
      addressNeighborhood: fornecedor.address_neighborhood || '',
      addressCity: fornecedor.address_city || '',
      addressState: fornecedor.address_state || '',
      addressZipcode: fornecedor.address_zipcode || '',
      addressCountry: fornecedor.address_country || 'BR',
      type: fornecedor.type || 'F',
      status: fornecedor.status || 'active'
    })
    setModalOpen(true)
  }

  const handleDelete = async (fornecedor) => {
    if (window.confirm(`Deseja realmente excluir o fornecedor ${fornecedor.name}?`)) {
      try {
        const response = await supplierService.delete(fornecedor.id)
        if (response.success) {
          loadFornecedores()
        }
      } catch (error) {
        console.error('Erro ao excluir fornecedor:', error)
        alert('Erro ao excluir fornecedor')
      }
    }
  }

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      cpfCnpj: '',
      addressStreet: '',
      addressNumber: '',
      addressComplement: '',
      addressNeighborhood: '',
      addressCity: '',
      addressState: '',
      addressZipcode: '',
      addressCountry: 'BR',
      type: 'F',
      status: 'active'
    })
    setEditingFornecedor(null)
    setErrors({})
  }

  const columns = [
    { key: 'name', label: 'Nome', sortable: true },
    { key: 'email', label: 'E-mail', sortable: true },
    { key: 'phone', label: 'Telefone' },
    { key: 'cpf_cnpj', label: 'CPF/CNPJ' },
    {
      key: 'status',
      label: 'Status',
      render: (value) => {
        const statusMap = {
          active: { label: 'Ativo', class: 'active' },
          inactive: { label: 'Inativo', class: 'inactive' },
          blocked: { label: 'Bloqueado', class: 'blocked' }
        }
        const status = statusMap[value] || statusMap.inactive
        return (
          <span className={`status-badge ${status.class}`}>
            {status.label}
          </span>
        )
      }
    }
  ]

  const actions = [
    {
      label: 'Editar',
      icon: Edit,
      variant: 'primary',
      onClick: handleEdit
    },
    {
      label: 'Excluir',
      icon: Trash2,
      variant: 'danger',
      onClick: handleDelete
    }
  ]

  // --- Funções de importação Excel ---
  const handleImportFileSelect = (e) => {
    const file = e.target.files?.[0]
    if (file) setImportFile(file)
  }

  const handleImportDrop = (e) => {
    e.preventDefault()
    const file = e.dataTransfer.files?.[0]
    if (file) setImportFile(file)
  }

  const handleImportPreview = async () => {
    if (!importFile) return
    setImportLoading(true)
    try {
      const response = await supplierService.previewImport(importFile)
      if (response.success && response.data?.success !== false) {
        setImportPreview(response.data)
        setImportStep('preview')
      } else {
        alert(response.data?.error || response.message || 'Erro ao processar arquivo')
      }
    } catch (error) {
      console.error('Erro no preview:', error)
      alert(error.response?.data?.message || error.message || 'Erro ao processar arquivo')
    } finally {
      setImportLoading(false)
    }
  }

  const handleImportExecute = async () => {
    if (!importFile) return
    setImportLoading(true)
    try {
      const response = await supplierService.executeImport(importFile)
      if (response.success) {
        setImportResult(response.data)
        setImportStep('result')
        loadFornecedores()
      } else {
        alert(response.data?.error || 'Erro ao importar')
      }
    } catch (error) {
      console.error('Erro na importação:', error)
      alert(error.response?.data?.message || error.message || 'Erro ao importar')
    } finally {
      setImportLoading(false)
    }
  }

  const handleImportClose = () => {
    setImportModalOpen(false)
    setImportStep('upload')
    setImportFile(null)
    setImportPreview(null)
    setImportResult(null)
    if (importFileInputRef.current) importFileInputRef.current.value = ''
  }

  return (
    <div className="fornecedores-page">
      <div className="page-header">
        <div>
          <h1>Fornecedores</h1>
          <p>Gerencie seus fornecedores</p>
        </div>
        <div className="page-header-actions">
          <button
            className="btn-secondary"
            onClick={() => setImportModalOpen(true)}
          >
            <Upload size={18} />
            Importar
          </button>
          <button
            className="btn-primary"
            onClick={() => {
              resetForm()
              setModalOpen(true)
            }}
          >
            <Plus size={18} />
            Novo Fornecedor
          </button>
        </div>
      </div>

      <div className="page-filters">
        <div className="search-input-wrapper">
          <Search size={18} className="search-icon" />
          <input
            type="text"
            placeholder="Buscar fornecedores..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      <DataTable
        columns={columns}
        data={fornecedores}
        loading={loading}
        actions={actions}
        emptyMessage="Nenhum fornecedor encontrado"
      />

      <Modal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false)
          resetForm()
        }}
        title={editingFornecedor ? 'Editar Fornecedor' : 'Novo Fornecedor'}
        size="medium"
        footer={
          <>
            <button
              className="btn-secondary"
              onClick={() => {
                setModalOpen(false)
                resetForm()
              }}
            >
              Cancelar
            </button>
            <button
              className="btn-primary"
              onClick={handleSubmit}
            >
              {editingFornecedor ? 'Salvar' : 'Criar'}
            </button>
          </>
        }
      >
        <form onSubmit={handleSubmit}>
          <FormField
            label="Nome"
            name="name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            error={errors.name}
            required
          />

          <FormField
            label="E-mail"
            name="email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            error={errors.email}
          />

          <FormField
            label="Telefone"
            name="phone"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            error={errors.phone}
            mask="phone"
          />

          <FormField
            label="CPF/CNPJ"
            name="cpfCnpj"
            value={formData.cpfCnpj}
            onChange={(e) => {
              const value = e.target.value
              const cleanValue = value.replace(/\D/g, '')
              const masked = cleanValue.length <= 11
                ? cleanValue.replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d{1,2})$/, '$1-$2')
                : cleanValue.replace(/(\d{2})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1/$2').replace(/(\d{4})(\d)/, '$1-$2')
              setFormData({ ...formData, cpfCnpj: masked, type: cleanValue.length <= 11 ? 'F' : 'J' })
            }}
            error={errors.cpfCnpj}
            placeholder="000.000.000-00 ou 00.000.000/0000-00"
          />

          <FormField
            label="Tipo"
            name="type"
            type="select"
            value={formData.type}
            onChange={(e) => setFormData({ ...formData, type: e.target.value })}
            options={[
              { value: 'F', label: 'Pessoa Física' },
              { value: 'J', label: 'Pessoa Jurídica' }
            ]}
          />

          <FormField
            label="Rua"
            name="addressStreet"
            value={formData.addressStreet}
            onChange={(e) => setFormData({ ...formData, addressStreet: e.target.value })}
            error={errors.addressStreet}
          />

          <div className="form-row">
            <FormField
              label="Número"
              name="addressNumber"
              value={formData.addressNumber}
              onChange={(e) => setFormData({ ...formData, addressNumber: e.target.value })}
              error={errors.addressNumber}
            />

            <FormField
              label="Complemento"
              name="addressComplement"
              value={formData.addressComplement}
              onChange={(e) => setFormData({ ...formData, addressComplement: e.target.value })}
              error={errors.addressComplement}
            />
          </div>

          <FormField
            label="Bairro"
            name="addressNeighborhood"
            value={formData.addressNeighborhood}
            onChange={(e) => setFormData({ ...formData, addressNeighborhood: e.target.value })}
            error={errors.addressNeighborhood}
          />

          <div className="form-row">
            <FormField
              label="Cidade"
              name="addressCity"
              value={formData.addressCity}
              onChange={(e) => setFormData({ ...formData, addressCity: e.target.value })}
              error={errors.addressCity}
            />

            <FormField
              label="Estado"
              name="addressState"
              value={formData.addressState}
              onChange={(e) => setFormData({ ...formData, addressState: e.target.value.toUpperCase() })}
              error={errors.addressState}
              maxLength={2}
            />

            <FormField
              label="CEP"
              name="addressZipcode"
              value={formData.addressZipcode}
              onChange={(e) => setFormData({ ...formData, addressZipcode: e.target.value })}
              error={errors.addressZipcode}
              mask="cep"
            />
          </div>

          <FormField
            label="Status"
            name="status"
            type="select"
            value={formData.status}
            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            options={[
              { value: 'active', label: 'Ativo' },
              { value: 'inactive', label: 'Inativo' },
              { value: 'blocked', label: 'Bloqueado' }
            ]}
          />

          {errors.submit && (
            <div className="form-error-message">{errors.submit}</div>
          )}
        </form>
      </Modal>

      {/* Modal de Importação Excel */}
      <Modal
        isOpen={importModalOpen}
        onClose={handleImportClose}
        title="Importar Fornecedores"
        size="large"
        footer={
          <>
            {importStep === 'upload' && (
              <>
                <button className="btn-secondary" onClick={handleImportClose}>Cancelar</button>
                <button className="btn-primary" onClick={handleImportPreview} disabled={!importFile || importLoading}>
                  {importLoading ? 'Processando...' : 'Visualizar'}
                </button>
              </>
            )}
            {importStep === 'preview' && (
              <>
                <button className="btn-secondary" onClick={() => { setImportStep('upload'); setImportPreview(null) }}>Voltar</button>
                <button className="btn-primary" onClick={handleImportExecute} disabled={importLoading || !importPreview?.validRows}>
                  {importLoading ? 'Importando...' : `Importar ${importPreview?.validRows || 0} fornecedores`}
                </button>
              </>
            )}
            {importStep === 'result' && (
              <button className="btn-primary" onClick={handleImportClose}>Fechar</button>
            )}
          </>
        }
      >
        {importStep === 'upload' && (
          <div className="import-upload-area">
            <input
              ref={importFileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleImportFileSelect}
              style={{ display: 'none' }}
            />
            <div
              className="import-dropzone"
              onDrop={handleImportDrop}
              onDragOver={(e) => e.preventDefault()}
              onClick={() => importFileInputRef.current?.click()}
            >
              <FileSpreadsheet size={48} strokeWidth={1.5} />
              <p><strong>Arraste o arquivo Excel aqui</strong></p>
              <p>ou clique para selecionar</p>
              <span className="import-dropzone-hint">Formatos aceitos: .xlsx, .xls</span>
            </div>
            {importFile && (
              <div className="import-file-selected">
                <FileSpreadsheet size={16} />
                <span>{importFile.name}</span>
                <button onClick={() => setImportFile(null)}><X size={14} /></button>
              </div>
            )}
            <div className="import-instructions">
              <h4>Formato esperado da planilha:</h4>
              <table className="import-format-table">
                <thead>
                  <tr>
                    <th>Nome/Razão social</th>
                    <th>Apelido</th>
                    <th>CPF/CNPJ</th>
                    <th>Tipo pessoa</th>
                    <th>Cidade</th>
                    <th>Estado</th>
                    <th>E-mail</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>DELLAMED S.A.</td>
                    <td>Dellamed</td>
                    <td>12345678000190</td>
                    <td>Jurídica</td>
                    <td>São Paulo</td>
                    <td>SP</td>
                    <td>contato@...</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {importStep === 'preview' && importPreview && (
          <div className="import-preview-area">
            <div className="import-preview-summary">
              <div className="import-stat">
                <span className="import-stat-value">{importPreview.totalRows}</span>
                <span className="import-stat-label">Total de linhas</span>
              </div>
              <div className="import-stat success">
                <span className="import-stat-value">{importPreview.validRows}</span>
                <span className="import-stat-label">Válidas</span>
              </div>
              {importPreview.errorRows > 0 && (
                <div className="import-stat error">
                  <span className="import-stat-value">{importPreview.errorRows}</span>
                  <span className="import-stat-label">Com erros</span>
                </div>
              )}
            </div>

            <div className="import-preview-table-wrapper">
              <table className="import-preview-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Nome</th>
                    <th>CPF/CNPJ</th>
                    <th>Tipo</th>
                    <th>Cidade/UF</th>
                    <th>E-mail</th>
                    <th>Telefone</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {importPreview.rows.map((row) => (
                    <tr key={row.rowNumber} className={!row.valid ? 'import-row-error' : row.warnings.length > 0 ? 'import-row-warning' : ''}>
                      <td>{row.rowNumber}</td>
                      <td>{row.name}</td>
                      <td>{row.cpfCnpj || '-'}</td>
                      <td>{row.typeLabel}</td>
                      <td>{row.addressCity ? `${row.addressCity}/${row.addressState}` : '-'}</td>
                      <td>{row.email || '-'}</td>
                      <td>{row.phone || '-'}</td>
                      <td>
                        {row.valid ? (
                          <span className="import-badge ok">
                            <CheckCircle2 size={12} /> OK
                            {row.warnings.length > 0 && <span title={row.warnings.join(', ')}> ⚠</span>}
                          </span>
                        ) : (
                          <span className="import-badge err" title={row.errors.join(', ')}><AlertCircle size={12} /> Erro</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {importStep === 'result' && importResult && (
          <div className="import-result-area">
            <div className="import-result-icon">
              <CheckCircle2 size={48} />
            </div>
            <h3>Importação Concluída</h3>
            <div className="import-result-stats">
              <p><strong>{importResult.created}</strong> fornecedores criados com sucesso</p>
              {importResult.duplicates > 0 && <p><strong>{importResult.duplicates}</strong> ignorados (CPF/CNPJ já cadastrado)</p>}
              {importResult.skipped > 0 && <p><strong>{importResult.skipped}</strong> linhas ignoradas (erros de validação)</p>}
              {importResult.errors?.length > 0 && (
                <div className="import-result-errors">
                  <p>Erros durante a importação:</p>
                  <ul>
                    {importResult.errors.map((err, i) => (
                      <li key={i}>Linha {err.rowNumber}: {err.error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}

export default FornecedoresPage

